import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const ProblemSolution = () => {
  const [activeHotspot, setActiveHotspot] = useState(null);
  const modalRef = useRef(null);
  const navigate = useNavigate();

  // Handle click outside modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setActiveHotspot(null);
      }
    };

    if (activeHotspot) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeHotspot]);

  const problems = [
    {
      id: 1,
      title: "Unreliable Suppliers",
      description: "Finding trustworthy African suppliers who deliver consistent quality and meet deadlines is a major challenge for international buyers.",
      icon: "AlertTriangle",
      position: { x: 20, y: 30 },
      details: "67% of importers report supplier reliability issues, leading to delayed shipments and quality inconsistencies that damage business relationships."
    },
    {
      id: 2,
      title: "Complex Logistics",
      description: "Navigating African export regulations, customs procedures, and shipping logistics without local expertise creates costly delays.",
      icon: "Truck",
      position: { x: 60, y: 25 },
      details: "Average shipping delays of 2-3 weeks due to documentation issues and lack of logistics coordination, increasing costs by 25-40%."
    },
    {
      id: 3,
      title: "Quality Concerns",
      description: "Inconsistent product quality and lack of proper quality assurance processes lead to rejected shipments and financial losses.",
      icon: "XCircle",
      position: { x: 40, y: 60 },
      details: "30% of first-time African imports face quality issues, resulting in returns, refunds, and damaged business reputation."
    },
    {
      id: 4,
      title: "Communication Barriers",
      description: "Language differences, time zone challenges, and cultural misunderstandings complicate business relationships.",
      icon: "MessageSquare",
      position: { x: 75, y: 50 },
      details: "Communication delays average 24-48 hours, slowing decision-making and creating frustration for time-sensitive orders."
    }
  ];

  const solutions = [
    {
      id: 1,
      title: "Agricultural Expertise",
      description: "Specialized knowledge in agricultural product sourcing, processing, and quality standards specifically for corn, moringa, and shea products.",
      icon: "Shield",
      benefit: "Focused specialization"
    },
    {
      id: 2,
      title: "Value-Added Processing",
      description: "Modern processing capabilities to deliver refined, value-added agricultural products rather than just raw materials.",
      icon: "Globe",
      benefit: "Higher quality standards"
    },
    {
      id: 3,
      title: "Quality Commitment",
      description: "Comprehensive quality control processes and certifications to ensure our agricultural products meet international standards.",
      icon: "Award",
      benefit: "Premium product guarantee"
    },
    {
      id: 4,
      title: "Customer Partnership",
      description: "Dedicated support and partnership approach to build long-term relationships with our international clients.",
      icon: "Handshake",
      benefit: "Personalized service"
    }
  ];

  return (
    <section className="pt-20 pb-10 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Solving Agricultural Export Challenges
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            As a new company, we're uniquely positioned to address common agricultural export challenges with modern solutions and fresh approaches.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Problems Side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="mb-8">
              <h3 className="text-3xl font-bold text-error mb-4 flex items-center">
                <Icon name="AlertCircle" size={32} className="mr-3" />
                Common Export Challenges
              </h3>
              <p className="text-text-secondary">
                International buyers face these recurring issues when sourcing from Africa
              </p>
            </div>

            {/* Interactive Problem Visualization */}
            <div className="relative bg-gradient-to-br from-error-50 to-warning-50 rounded-2xl p-8 mb-8">
              <Image
                src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=400&fit=crop"
                alt="Export Challenges"
                className="w-full h-64 object-cover rounded-lg opacity-60"
              />
              
              {/* Problem Hotspots */}
              {problems.map((problem) => (
                <motion.button
                  key={problem.id}
                  className="absolute w-8 h-8 bg-error rounded-full border-2 border-white shadow-lg hover:scale-125 transition-all duration-300 flex items-center justify-center"
                  style={{
                    left: `${problem.position.x}%`,
                    top: `${problem.position.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                  onClick={() => setActiveHotspot(activeHotspot === problem.id ? null : problem.id)}
                  whileHover={{ scale: 1.25 }}
                  whileTap={{ scale: 1.1 }}
                >
                  <Icon name={problem.icon} size={16} color="white" />
                  <motion.div
                    className="absolute inset-0 bg-error rounded-full opacity-50"
                    animate={{ scale: [1, 1.5, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.button>
              ))}

              {/* Problem Details Popup */}
              {activeHotspot && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  ref={modalRef}
                  className="absolute top-4 right-4 glass-card p-4 max-w-xs z-10"
                >
                  {(() => {
                    const problem = problems.find(p => p.id === activeHotspot);
                    return (
                      <div>
                        <h4 className="font-semibold text-primary mb-2">{problem.title}</h4>
                        <p className="text-sm text-text-secondary mb-2">{problem.description}</p>
                        <p className="text-xs text-error font-medium">{problem.details}</p>
                      </div>
                    );
                  })()}
                </motion.div>
              )}
            </div>

            {/* Problem List */}
            <div className="space-y-4">
              {problems.map((problem, index) => (
                <motion.div
                  key={problem.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start space-x-4 p-4 bg-error-50 rounded-lg border-l-4 border-error"
                >
                  <div className="w-10 h-10 bg-error rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={problem.icon} size={20} color="white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary mb-1">{problem.title}</h4>
                    <p className="text-sm text-text-secondary">{problem.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Solutions Side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="mb-8">
              <h3 className="text-3xl font-bold text-success mb-4 flex items-center">
                <Icon name="CheckCircle" size={32} className="mr-3" />
                Our Proven Solutions
              </h3>
              <p className="text-text-secondary">
                Comprehensive solutions designed to eliminate export challenges
              </p>
            </div>

            {/* Solution Visualization */}
            <div className="relative bg-gradient-to-br from-success-50 to-primary-50 rounded-2xl p-8 mb-8">
              <Image
                src="/assets/images/ChatGPT Image Jun 19 2025 Nigerian Export Process.png"
                alt="Nigerian Export Process Solutions"
                className="w-full h-64 object-cover rounded-lg"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-success/20 to-transparent rounded-lg"></div>
            </div>

            {/* Solutions List */}
            <div className="space-y-4">
              {solutions.map((solution, index) => (
                <motion.div
                  key={solution.id}
                  initial={{ opacity: 0, x: 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.15, ease: "easeOut" }}
                  className="glass-card p-6 hover:shadow-elevation-md transition-all duration-500"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-success to-success-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={solution.icon} size={24} color="white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-primary mb-2">{solution.title}</h4>
                      <p className="text-text-secondary mb-3">{solution.description}</p>
                      <div className="flex items-center space-x-2">
                        <Icon name="TrendingUp" size={16} className="text-success" />
                        <span className="text-sm font-medium text-success">{solution.benefit}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-8 text-center"
            >
              <button
                onClick={() => navigate('/contact')}
                className="btn-accent px-8 py-4 text-lg font-semibold flex items-center space-x-3 mx-auto"
              >
                <Icon name="ArrowRight" size={20} />
                <span>Experience the Difference</span>
              </button>
              <p className="text-sm text-text-secondary mt-3">
                Free consultation • No commitment required
              </p>
            </motion.div>
          </motion.div>
        </div>

      </div>
    </section>
  );
};

export default ProblemSolution;